import { Plus } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/Button";
import { useConfirm } from "../components/ui/ConfirmDialog";
import { EmptyState, ErrorState, Skeleton } from "../components/ui/Feedback";
import { TBody, TD, TH, THead, TR, Table } from "../components/ui/Table";
import { useToast } from "../components/ui/Toast";
import { useCampaigns } from "../features/campaigns/useCampaigns";
import { ExperimentDialog } from "../features/experiments/ExperimentDialog";
import { useDeleteExperiment, useExperiments } from "../features/experiments/useExperiments";
import type { Campaign, Experiment } from "../lib/types";

type Editing = { experiment: Experiment | null } | null;

const campaignsIn = (experiment: Experiment, campaigns: readonly Campaign[]): Campaign[] => {
	const groupIds = new Set(experiment.groups.map((group) => group.id));
	return campaigns.filter(
		(campaign) => campaign.experimentGroupId && groupIds.has(campaign.experimentGroupId),
	);
};

export const ExperimentsPage = () => {
	const experiments = useExperiments();
	const campaigns = useCampaigns();
	const remove = useDeleteExperiment();
	const confirm = useConfirm();
	const toast = useToast();
	const [editing, setEditing] = useState<Editing>(null);

	const destroy = (experiment: Experiment) =>
		confirm({
			title: `Delete “${experiment.title}”?`,
			description: "The test stays in Intelligems. Only the mapping here is removed.",
			onConfirm: () =>
				remove.mutate(experiment.id, {
					onSuccess: () => toast.push("Test deleted"),
					onError: (error) => toast.push(error.message, "error"),
				}),
		});

	return (
		<div className="space-y-6">
			<div className="flex flex-wrap items-end justify-between gap-4">
				<div>
					<h2 className="text-xl font-semibold tracking-tight text-ink">A/B tests</h2>
					<p className="mt-1 max-w-2xl text-sm text-ink-muted">
						Intelligems decides which group each shopper is in and measures the results. Map its
						groups here, then pick a group on each campaign under Schedule. A campaign with no group
						is shown to everyone.
					</p>
				</div>
				<Button onClick={() => setEditing({ experiment: null })}>
					<Plus className="h-4 w-4" strokeWidth={2} />
					Connect test
				</Button>
			</div>

			{editing ? (
				<ExperimentDialog experiment={editing.experiment} onClose={() => setEditing(null)} />
			) : null}

			{experiments.isError ? (
				<ErrorState title="Could not load tests" description={experiments.error.message} />
			) : null}

			{experiments.isPending ? <Skeleton className="h-40 w-full" /> : null}

			{experiments.data && experiments.data.items.length === 0 ? (
				<EmptyState
					title="No tests yet"
					description="In Intelligems, create a Content test with Onsite Edits and leave every group without edits. Then connect it here."
				/>
			) : null}

			{experiments.data && experiments.data.items.length > 0 ? (
				<div className="overflow-hidden rounded-md border border-line">
					<Table>
						<THead>
							<TR>
								<TH>Test</TH>
								<TH>Groups</TH>
								<TH>Campaigns</TH>
								<TH>Outside the test</TH>
								<TH className="w-36" />
							</TR>
						</THead>
						<TBody>
							{experiments.data.items.map((experiment) => {
								const gated = campaignsIn(experiment, campaigns.data?.items ?? []);
								return (
									<TR key={experiment.id} className="hover:bg-raised">
										<TD className="font-medium">{experiment.title}</TD>
										<TD className="text-ink-muted">
											{experiment.groups.map((group) => group.name).join(", ")}
										</TD>
										<TD className="text-ink-muted">
											{gated.length === 0
												? "None yet"
												: gated.map((campaign, index) => (
														<span key={campaign.id}>
															{index > 0 ? ", " : null}
															<Link
																to={`/campaigns/${campaign.id}`}
																className="underline-offset-4 hover:text-ink hover:underline"
															>
																{campaign.title}
															</Link>
														</span>
													))}
										</TD>
										<TD className="text-right">
											<Button variant="ghost" size="sm" onClick={() => setEditing({ experiment })}>
												Edit
											</Button>
											<Button variant="ghost" size="sm" onClick={() => destroy(experiment)}>
												Delete
											</Button>
										</TD>
									</TR>
								);
							})}
						</TBody>
					</Table>
				</div>
			) : null}
		</div>
	);
};
